import { useState, useMemo } from "react"
import { useQuery, useQueryClient } from "react-query"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { PageView, PageTopBar } from "../../components"
import { Loader } from "../../components/Loader"
import { fetchOperatorRates } from "../../services/workforceServices"
import { WorkforceTabs } from "./WorkforceTabs"
import { RateFormSlidePanel } from "./components/RateFormSlidePanel"
import { CONTRACT_TYPE_BADGE_CLASS, formatMoney } from "../../utils/workforceUtils"

const CONTRACT_TYPES = ['IPS', 'MP', 'ADECCO', 'CUBBO']

const StatusBadge = ({ status, label }) => {
    const cls = status === 'active'
        ? 'bg-green-50 text-green-700 border-green-200'
        : 'bg-gray-50 text-gray-500 border-gray-200'
    return (
        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${cls}`}>{label}</span>
    )
}

const ContractBadge = ({ contract }) => {
    const cls = CONTRACT_TYPE_BADGE_CLASS[contract] || CONTRACT_TYPE_BADGE_CLASS.CUBBO
    return <span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${cls}`}>{contract}</span>
}

export const OperatorRatesContainer = () => {
    const { warehouse_id } = useParams()
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const [operatorTypeFilter, setOperatorTypeFilter] = useState('')
    const [contractFilter, setContractFilter] = useState('')
    const [panelMode, setPanelMode] = useState(null) // null | 'create' | { mode: 'fix', rate }
    const [expandedGroups, setExpandedGroups] = useState({})

    const ratesQueryKey = ['workforce', warehouse_id, 'rates', { operator_type: operatorTypeFilter, contract_type: contractFilter }]
    const { data: rates, isLoading, isError, error } = useQuery(
        ratesQueryKey,
        () => fetchOperatorRates(warehouse_id, {
            ...(operatorTypeFilter ? { operator_type: operatorTypeFilter } : {}),
            ...(contractFilter ? { contract_type: contractFilter } : {}),
        }),
        { keepPreviousData: true }
    )

    const { data: allRatesData } = useQuery(
        ['workforce', warehouse_id, 'rates', {}],
        () => fetchOperatorRates(warehouse_id, {}),
        { staleTime: 1000 * 60 * 10, refetchOnWindowFocus: false }
    )

    const operatorTypes = useMemo(() => {
        const raw = Array.isArray(allRatesData) ? allRatesData : (allRatesData?.operator_rates || allRatesData?.rates || [])
        return [...new Set(raw.map((r) => r.operator_type).filter(Boolean))].sort()
    }, [allRatesData])

    const groups = useMemo(() => {
        const raw = Array.isArray(rates) ? rates : (rates?.operator_rates || rates?.rates || [])
        const byKey = new Map()
        for (const r of raw) {
            const key = `${r.operator_type}__${r.contract_type}`
            if (!byKey.has(key)) byKey.set(key, [])
            byKey.get(key).push(r)
        }
        return Array.from(byKey.entries()).map(([key, items]) => {
            const sorted = [...items].sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))
            return {
                key,
                operator_type: sorted[0].operator_type,
                contract_type: sorted[0].contract_type,
                active: sorted[0],
                history: sorted.slice(1),
            }
        })
    }, [rates])

    const hasSeededNightRates = useMemo(() => {
        const raw = Array.isArray(rates) ? rates : (rates?.operator_rates || rates?.rates || [])
        return raw.some((r) =>
            r.gross_night_rate != null && r.gross_day_rate != null &&
            Number(r.gross_night_rate) === Number(r.gross_day_rate) &&
            Number(r.gross_night_extra_hour_rate) === Number(r.gross_day_extra_hour_rate)
        )
    }, [rates])

    const onPanelClose = (didChange) => {
        setPanelMode(null)
        if (didChange) queryClient.invalidateQueries(ratesQueryKey)
    }

    return (
        <>
            <PageView
                topMenu={
                    <PageTopBar>
                        <div className="text-lg font-semibold">{t('workforce.rates.title')}</div>
                        <button
                            type="button"
                            onClick={() => setPanelMode('create')}
                            className="whitespace-nowrap inline-flex items-center rounded-lg border border-transparent bg-yellow-500 px-3 py-2 text-sm font-medium text-white hover:bg-yellow-600"
                        >
                            + {t('workforce.rates.add_new')}
                        </button>
                    </PageTopBar>
                }
                topMenuFullWidth={true}
                childrenFullWidth={true}
                noWrapper={false}
            >
                <WorkforceTabs />

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900 mt-4">
                    <div><strong>{t('workforce.rates.info_add_title')}</strong> — {t('workforce.rates.info_add_body')}</div>
                    <div className="mt-1"><strong>{t('workforce.rates.info_fix_title')}</strong> — {t('workforce.rates.info_fix_body')}</div>
                </div>

                {hasSeededNightRates && (
                    <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                        {t('workforce.rates.night_seed_banner')}
                    </div>
                )}

                <div className="flex flex-wrap gap-2 mt-4 items-center">
                    <select
                        value={operatorTypeFilter}
                        onChange={(e) => setOperatorTypeFilter(e.target.value)}
                        className="border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
                    >
                        <option value="">{t('workforce.rates.all_types')}</option>
                        {operatorTypes.map((ot) => (<option key={ot} value={ot}>{ot}</option>))}
                    </select>
                    <select
                        value={contractFilter}
                        onChange={(e) => setContractFilter(e.target.value)}
                        className="border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
                    >
                        <option value="">{t('workforce.rates.all_contracts')}</option>
                        {CONTRACT_TYPES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                </div>

                {isLoading ? (
                    <Loader show={true} />
                ) : isError ? (
                    <div className="mt-4 p-3 rounded bg-red-50 text-red-700 text-sm">{error?.message || t('workforce.errors.generic')}</div>
                ) : groups.length === 0 ? (
                    <div className="mt-4 p-6 text-center text-gray-500 border rounded bg-white">
                        {t('workforce.rates.empty')}
                    </div>
                ) : (
                    <div className="bg-white mt-4 border rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th rowSpan={2} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('workforce.rates.operator_type')}</th>
                                    <th rowSpan={2} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('workforce.rates.contract')}</th>
                                    <th colSpan={2} className="px-4 pt-2 text-center text-xs font-semibold text-yellow-700 uppercase border-l border-gray-200">{t('workforce.rates.day_rates')}</th>
                                    <th colSpan={2} className="px-4 pt-2 text-center text-xs font-semibold text-indigo-700 uppercase border-l border-gray-200">{t('workforce.rates.night_rates')}</th>
                                    <th rowSpan={2} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase align-bottom border-l border-gray-200">{t('workforce.rates.effective_from')}</th>
                                    <th rowSpan={2} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase align-bottom">{t('workforce.rates.status')}</th>
                                    <th rowSpan={2} className="px-4 py-2"></th>
                                </tr>
                                <tr>
                                    <th className="px-3 pb-2 text-right text-[11px] font-medium text-gray-500 normal-case border-l border-gray-200">{t('workforce.rates.daily_short')}</th>
                                    <th className="px-3 pb-2 text-right text-[11px] font-medium text-gray-500 normal-case">{t('workforce.rates.extra_hour_short')}</th>
                                    <th className="px-3 pb-2 text-right text-[11px] font-medium text-gray-500 normal-case border-l border-gray-200">{t('workforce.rates.daily_short')}</th>
                                    <th className="px-3 pb-2 text-right text-[11px] font-medium text-gray-500 normal-case">{t('workforce.rates.extra_hour_short')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {groups.map((g) => {
                                    const expanded = !!expandedGroups[g.key]
                                    const renderAmountCells = (r, muted) => (
                                        <>
                                            <td className={`px-3 py-2 text-sm text-right font-mono border-l border-gray-100 ${muted ? '' : ''}`}>{formatMoney(r.gross_day_rate, r.currency)}</td>
                                            <td className="px-3 py-2 text-sm text-right font-mono">{formatMoney(r.gross_day_extra_hour_rate, r.currency)}</td>
                                            <td className="px-3 py-2 text-sm text-right font-mono border-l border-gray-100">{formatMoney(r.gross_night_rate, r.currency)}</td>
                                            <td className="px-3 py-2 text-sm text-right font-mono">{formatMoney(r.gross_night_extra_hour_rate, r.currency)}</td>
                                        </>
                                    )
                                    return [
                                        <tr key={`${g.key}-active`}>
                                            <td className="px-4 py-2 text-sm">{g.operator_type}</td>
                                            <td className="px-4 py-2 text-sm"><ContractBadge contract={g.contract_type} /></td>
                                            {renderAmountCells(g.active, false)}
                                            <td className="px-4 py-2 text-sm border-l border-gray-100">{g.active.effective_from}</td>
                                            <td className="px-4 py-2 text-sm"><StatusBadge status="active" label={t('workforce.rates.status_active')} /></td>
                                            <td className="px-4 py-2 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => setPanelMode({ mode: 'fix', rate: g.active })}
                                                    className="text-xs border border-gray-300 rounded-md px-2 py-1 text-gray-700 hover:bg-gray-50"
                                                >
                                                    {t('workforce.rates.fix_mistake')}
                                                </button>
                                                {g.history.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                                                        className="ml-2 text-xs text-indigo-600 hover:text-indigo-800"
                                                    >
                                                        {expanded ? t('workforce.rates.hide_history') : t('workforce.rates.show_history', { count: g.history.length })}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>,
                                        ...(expanded ? g.history.map((h) => (
                                            <tr key={`${g.key}-h-${h.id}`} className="text-gray-400 bg-gray-50">
                                                <td className="px-4 py-2 text-sm">{h.operator_type}</td>
                                                <td className="px-4 py-2 text-sm"><ContractBadge contract={h.contract_type} /></td>
                                                {renderAmountCells(h, true)}
                                                <td className="px-4 py-2 text-sm border-l border-gray-100">{h.effective_from}</td>
                                                <td className="px-4 py-2 text-sm"><StatusBadge status="history" label={t('workforce.rates.status_history')} /></td>
                                                <td className="px-4 py-2 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPanelMode({ mode: 'fix', rate: h })}
                                                        className="text-xs border border-gray-300 rounded-md px-2 py-1 text-gray-700 hover:bg-gray-50"
                                                    >
                                                        {t('workforce.rates.fix_mistake')}
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : []),
                                    ]
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </PageView>

            {panelMode && (
                <RateFormSlidePanel
                    mode={panelMode === 'create' ? 'create' : 'fix'}
                    rate={panelMode === 'create' ? null : panelMode.rate}
                    warehouse_id={warehouse_id}
                    operatorTypes={operatorTypes}
                    contractTypes={CONTRACT_TYPES}
                    onClose={onPanelClose}
                />
            )}
        </>
    )
}
